import {
  ASSIGN_EXISTING_VEHICLE,
  CLEAR_ACTIVE_VEHICLE,
  FLAG_UPDATE_VEHICLES,
  MEMBER_INFO,
  MEMBERS_SEARCH,
  MEMBERS_SEARCH_RESET,
  REMOVE_VEHICLE,
  SET_ELIGIBILITY_VEHICLE,
  SET_PENDING_GRACE_SHEET_OPEN,
  SET_QUICK_RENEW_RETURN,
  QUICK_RENEW_CHECK_TIMED_OUT,
  SET_ENRICHED_VEHICLE,
  SET_VEHICLE,
  SET_VEHICLE_STATE_ONLY,
  VEHICLE_ADD,
  VEHICLE_DELETE,
  VEHICLE_LOAD,
  VEHICLE_UPDATE,
} from './member.actions'
import { MemberInfo, Vehicle, } from './member.types'
import { shouldOfferRenewalLink } from './member.utils'
import { PayloadedAction } from '../../shared/types'
import { CANCEL_EDITING_REQUEST } from '../../shared/actions/shared.actions'

export interface SearchMembersResults {
  searchId: string
  members: any[]
}

export interface MemberState {
  vehicles: VehicleGroup
  activeVehicle: Vehicle | null
  data: MemberInfo | null
  searchResults: SearchMembersResults | null
  flagUpdateVehicles: boolean
  pendingGraceSheetOpen: boolean
  quickRenewReturn: { memId: string; ets: string } | null
  paymentInProcessSheetOpen: boolean
  quickRenewTimedOut: boolean
  quickRenewResolving: boolean
}

export interface VehicleGroup {
  data: Array<Vehicle>
}

export const initialState: MemberState = {
  vehicles: {
    data: [],
  },
  activeVehicle: null,
  data: null,
  searchResults: null,
  flagUpdateVehicles: false,
  pendingGraceSheetOpen: false,
  quickRenewReturn: null,
  paymentInProcessSheetOpen: false,
  quickRenewTimedOut: false,
  quickRenewResolving: false,
}

export function vehicleReducer(
  state: VehicleGroup = initialState.vehicles,
  action: PayloadedAction
) {
  switch (action.type) {
    case VEHICLE_LOAD.SUCCESS:
      return {
        ...state,
        data: [...action.payload.vehicles],
      }
    case VEHICLE_ADD.SUCCESS:
      return {
        ...state,
        data: [...state.data, action.payload],
      }
    case VEHICLE_UPDATE.SUCCESS:
      const currentVehicles = state.data
      const index = currentVehicles.findIndex(
        (vehicle) => vehicle.id === action.payload.id
      )
      // Keep same item position, instead of moving the item to the of the list
      if (index !== -1) {
        currentVehicles[index] = {
          ...currentVehicles[index],
          ...action.payload,
        }
      }

      return {
        ...state,
        data: [...currentVehicles],
      }
    case VEHICLE_DELETE.SUCCESS:
      return {
        ...state,
        data: state.data.filter((vehicle) => vehicle.id !== action.payload),
      }
    default:
      return state
  }
}

export function reducer(
  state: MemberState | undefined = initialState,
  action: PayloadedAction
): MemberState {
  switch (action.type) {
    case MEMBER_INFO.SUCCESS: {
      const data = { ...action.payload }

      // A quick-renew return re-checks member details until the payment lands.
      // Once it times out the latch keeps a late in-flight response from
      // re-opening the sheet.
      if (!state.quickRenewReturn || state.quickRenewTimedOut) {
        return {
          ...state,
          data,
        }
      }

      const stillInRenewState = shouldOfferRenewalLink(data)

      return {
        ...state,
        data,
        paymentInProcessSheetOpen: stillInRenewState,
        quickRenewResolving: !stillInRenewState,
      }
    }
    case SET_ENRICHED_VEHICLE: {
      const enriched = action.payload.vehicle as Vehicle
      const matches = (v: Vehicle) =>
        (enriched.id && v.id === enriched.id) ||
        (v.year === enriched.year && v.make === enriched.make && v.model === enriched.model)
      return {
        ...state,
        activeVehicle: { ...state.activeVehicle, ...enriched },
        vehicles: {
          ...state.vehicles,
          data: (state.vehicles?.data ?? []).map((v) => (matches(v) ? { ...v, ...enriched } : v)),
        },
      }
    }
    case SET_VEHICLE:
    case SET_ELIGIBILITY_VEHICLE:
    case ASSIGN_EXISTING_VEHICLE:
    case SET_VEHICLE_STATE_ONLY:
      return {
        ...state,
        activeVehicle: { ...action.payload },
      }
    /**
     * This will grow eventually, move
     * CallVehicleRequest to a private VehicleReducer
     */
    case VEHICLE_LOAD.SUCCESS:
      return {
        ...state,
        vehicles: vehicleReducer(state.vehicles, action),
        flagUpdateVehicles: false
      }
    case VEHICLE_ADD.SUCCESS:
      return {
        ...state,
        activeVehicle: {
          ...state.activeVehicle,
          ...action.payload,
        },
        vehicles: vehicleReducer(state.vehicles, action),
      }
    case REMOVE_VEHICLE: {
      return {
        ...state,
        vehicles: vehicleReducer(state.vehicles, action),
      }
    }
    case VEHICLE_UPDATE.SUCCESS:
    case VEHICLE_DELETE.SUCCESS:
      return {
        ...state,
        vehicles: vehicleReducer(state.vehicles, action),
      }
    case MEMBERS_SEARCH.SUCCESS:
      return {
        ...state,
        searchResults: { ...action.payload },
      }
    case MEMBERS_SEARCH_RESET:
      return {
        ...state,
        searchResults: null,
      }
    case CANCEL_EDITING_REQUEST:
    case CLEAR_ACTIVE_VEHICLE:
      return {
        ...state,
        activeVehicle: null,
      }
    case FLAG_UPDATE_VEHICLES:
      return {
        ...state,
        flagUpdateVehicles: true
      }
    case SET_PENDING_GRACE_SHEET_OPEN:
      return {
        ...state,
        pendingGraceSheetOpen: action.payload,
      }
    case SET_QUICK_RENEW_RETURN:
      return {
        ...state,
        quickRenewReturn: { ...action.payload },
      }
    case QUICK_RENEW_CHECK_TIMED_OUT:
      return {
        ...state,
        paymentInProcessSheetOpen: false,
        quickRenewTimedOut: true,
        quickRenewResolving: false,
      }
    default:
      return state
  }
}
